import { Button } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { isDataAvailable } from "../../../common/utils/asyncDataUtils";
import authStore from "../../../stores/auth/authStore";
import promotionStore from "../../../stores/promotionStore";
import uiStore from "../../../stores/uiStore";
import { type AdventCalendarCampaign, AFTER_OPENING_BONUS, getAdventCalendarTickets } from "./adventCalendarUtils";

type AdventCalendarStickyProps = {
   campaign: AdventCalendarCampaign;
   isBeforeOpening: boolean;
};

const AdventCalendarSticky = observer(({ campaign, isBeforeOpening }: AdventCalendarStickyProps) => {
   const [shouldAnimateSignupIncrease, setShouldAnimateSignupIncrease] = useState<boolean>(false);
   const {
      total: numberOfSlips,
      bonusTickets: numberOfBonusSlips,
      hasReminder
   } = getAdventCalendarTickets(campaign, promotionStore.signUps.data);
   const hasLoadedSignUps = isDataAvailable(promotionStore.signUps);
   const company = authStore.currentCompany;
   const previous = useRef<{ company: string | null; numberOfSlips?: number }>({ company });

   useEffect(() => {
      if (!hasLoadedSignUps) {
         return;
      }
      const increased =
         previous.current.company === company &&
         previous.current.numberOfSlips !== undefined &&
         numberOfSlips > previous.current.numberOfSlips;
      previous.current = { company, numberOfSlips };
      if (increased) {
         const animationTimer = setTimeout(() => {
            setShouldAnimateSignupIncrease(true);
         }, 800);

         const resetTimer = setTimeout(() => {
            setShouldAnimateSignupIncrease(false);
         }, 2800);

         return () => {
            clearTimeout(animationTimer);
            clearTimeout(resetTimer);
         };
      }
   }, [numberOfSlips, company, hasLoadedSignUps]);

   return (
      <aside
         className={cn(
            "fixed z-40",
            "inset-x-0 bottom-0 rounded-none px-4 py-5 w-full",
            "md:inset-auto md:top-24 md:right-4 md:rounded-lg md:px-4 md:py-5 md:w-60",
            "bg-christmasButton text-ink-brand-inverted shadow-lg"
         )}
         aria-live="polite"
      >
         <div className="flex flex-col items-center">
            <div className="flex flex-row items-center gap-4 font-semibold">
               <span className="mt-1 text-lg whitespace-nowrap">Dine lodd</span>
               <span
                  id="advent-calendar-sticky"
                  className={cn("text-4xl", {
                     "dsp-celebrate-slip-increase": shouldAnimateSignupIncrease
                  })}
               >
                  {numberOfSlips}
               </span>
            </div>

            {hasReminder ? (
               <p className="mb-0 text-center">Du har samlet {numberOfBonusSlips} ekstra lodd.</p>
            ) : (
               !isBeforeOpening && (
                  <Button
                     variant="tertiary"
                     size="compact"
                     className="mb-0 underline text-ink-brand-inverted hover:bg-christmasButtonHover"
                     onClick={() => uiStore.toggleAdventCalendarReminderSignupModal()}
                  >
                     Vil du samle {AFTER_OPENING_BONUS} ekstra lodd?
                  </Button>
               )
            )}
         </div>
      </aside>
   );
});

export default AdventCalendarSticky;
